import { Button } from "components/Button/Button"
import { Form } from "components/Form/Form"
import { useNavigationBlocker } from "hooks/useNavigationBlocker"
import { ComponentProps, useEffect, useMemo, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { cn } from "styles/cn"
import { InputRTE, normalizeRichText } from "./InputRTE"

export function InputInline({
	defaultValue = "",
	placeholder,
	ariaLabel,
	readOnly,
	allowEmpty = true,
	isPending,
	isRemoving,
	errorMessage,
	className,
	onSave,
	onCancel,
	onRemove,
	onChange,
	...props
}: ComponentProps<"div"> & {
	defaultValue?: string
	placeholder?: string
	ariaLabel: string
	readOnly?: boolean
	allowEmpty?: boolean
	isPending?: boolean
	isRemoving?: boolean
	errorMessage?: string
	onSave: (value: string) => void
	onCancel: VoidFunction
	onRemove?: VoidFunction
	onChange?: VoidFunction
}) {
	const containerRef = useRef<HTMLDivElement>(null)
	const normalizedDefaultValue = useMemo(
		() => normalizeRichText(defaultValue),
		[defaultValue],
	)
	const [value, setValue] = useState(normalizedDefaultValue)
	const displayValue = readOnly ? normalizedDefaultValue : value
	const isDirty = value !== normalizedDefaultValue
	const isEmpty = value.trim().length === 0
	const saveDisabled = Boolean(
		readOnly || isPending || isRemoving || !isDirty || (!allowEmpty && isEmpty),
	)
	const saveLabel = isPending ? "Saving..." : "Save changes"
	const shouldBlockNavigation = !saveDisabled

	useHotkeys(
		"mod+enter",
		(e) => {
			if (!containerRef.current?.contains(document.activeElement)) {
				return
			}
			e.preventDefault()
			onSave(value)
		},
		{
			enabled: !saveDisabled,
			enableOnFormTags: true,
			enableOnContentEditable: true,
			preventDefault: false,
		},
	)

	useEffect(() => {
		setValue(normalizedDefaultValue)
	}, [normalizedDefaultValue, readOnly])

	if (readOnly && displayValue.length === 0) {
		return placeholder ? <p className="text-navy-60">{placeholder}</p> : null
	}

	return (
		<div
			{...props}
			ref={containerRef}
			className={cn("flex flex-col gap-2", className)}
		>
			{shouldBlockNavigation && <NavigationBlocker />}

			<Form.Item
				className="mb-0"
				validateStatus={!readOnly && errorMessage ? "error" : undefined}
				help={!readOnly ? errorMessage : undefined}
			>
				<InputRTE
					autoFocus={!readOnly}
					value={displayValue}
					aria-label={ariaLabel}
					placeholder={placeholder}
					minRows={readOnly ? 1 : 3}
					variant={readOnly ? "borderless" : "outline"}
					readOnly={readOnly}
					disabled={isPending || isRemoving}
					className={cn(readOnly && "p-0")}
					onChange={(nextValue) => {
						onChange?.()
						setValue(nextValue)
					}}
				/>
			</Form.Item>

			{!readOnly && (
				<div className="flex gap-3">
					{onRemove && (
						<Button danger loading={isRemoving} onClick={onRemove}>
							Remove
						</Button>
					)}
					<Button
						className="ml-auto"
						disabled={isPending || isRemoving}
						onClick={() => {
							setValue(normalizedDefaultValue)
							onCancel()
						}}
					>
						Cancel
					</Button>
					<Button
						type="primary"
						loading={isPending}
						disabled={saveDisabled}
						onClick={() => onSave(value)}
					>
						{saveLabel}
					</Button>
				</div>
			)}
		</div>
	)
}

function NavigationBlocker() {
	useNavigationBlocker(
		"You have unsaved changes. Are you sure you want to leave? Your changes will be lost.",
		true,
	)
	return null
}
